import { FC } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { HomeContent } from "@/constants/landingPages";

const stepIcons: Record<number, string> = {
  1: "💬",
  2: "⚙️",
  3: "🚀",
};

interface HowItWorksProps {
  content: HomeContent["howItWorks"];
}

export const HowItWorks: FC<HowItWorksProps> = ({ content }) => (
  <Box
    py={{ base: "16", md: "24" }}
    bgGradient="linear(to-b, white, gray.50)"
    position="relative"
    overflow="hidden"
  >
    <Container maxW="7xl" position="relative" zIndex={1}>
      <VStack spacing="16">
        {/* Section header */}
        <VStack spacing="4" textAlign="center" maxW="800px" mx="auto">
          <Heading as="h2" textStyle="heading36" color="gray.900">
            {content.title}
          </Heading>
          <Text textStyle="heading20" color="gray.600" lineHeight="1.7">
            {content.subtitle}
          </Text>
        </VStack>

        {/* Steps */}
        <VStack spacing="12" w="100%">
          {content.steps.map((step, index) => {
            const isEven = index % 2 === 0;
            const getGradient = () => {
              if (index === 0) return "linear-gradient(135deg, rgba(255,0,0,0.4), rgba(255,100,100,0.2))";
              if (index === 1) return "linear-gradient(135deg, rgba(0,0,255,0.4), rgba(100,100,255,0.2))";
              return "linear-gradient(135deg, rgba(0,200,200,0.4), rgba(100,255,200,0.2))";
            };
            return (
              <ScrollReveal key={`step-${step.id}`} w="100%">
                <Flex
                  direction={{ base: "column", lg: isEven ? "row" : "row-reverse" }}
                  align="center"
                  gap={{ base: "8", lg: "16" }}
                  w="100%"
                >
                {/* Step content */}
                <VStack
                  flex="1"
                  align={{ base: "center", lg: isEven ? "flex-start" : "flex-end" }}
                  spacing="4"
                  textAlign={{ base: "center", lg: isEven ? "left" : "right" }}
                >
                <Text
                  fontSize="6xl"
                  fontWeight="900"
                  color="gray.200"
                  lineHeight="1"
                >
                  {String(step.id).padStart(2, '0')}
                </Text>
                <Heading as="h3" textStyle="heading24" color="gray.900">
                  {step.title}
                </Heading>
                <Text
                  color="gray.600"
                  lineHeight="1.7"
                  fontSize="lg"
                  maxW="500px"
                >
                  {step.description}
                  </Text>
                </VStack>

                {/* Step visual */}
                <Flex
                  flex="1"
                  justify="center"
                  align="center"
                  position="relative"
                >
                  <Box
                  w={{ base: "280px", md: "350px" }}
                  h={{ base: "280px", md: "350px" }}
                  borderRadius="2xl"
                  bg="white"
                  border="2px solid"
                  borderColor="gray.200"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="8xl"
                  boxShadow="xl"
                  position="relative"
                  _before={{
                    content: '""',
                    position: "absolute",
                    inset: "-4px",
                    borderRadius: "2xl",
                    padding: "4px",
                    background: getGradient(),
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    opacity: 0.6,
                  }}
                >
                    {stepIcons[step.id] || "📦"}
                  </Box>

                  {/* Connecting line
                  {!isLast && (
                    <Box
                      display={{ base: "none", lg: "block" }}
                      position="absolute"
                      bottom="-12"
                      left="50%"
                      transform="translateX(-50%)"
                      w="2px"
                      h="48"
                      bg="gray.300"
                      _after={{
                        content: '""',
                        position: "absolute",
                        bottom: "-8px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "8px solid",
                        borderTopColor: "gray.300",
                      }}
                    />
                  )} */}
                </Flex>
              </Flex>
            </ScrollReveal>
          );
          })}
        </VStack>

        {/* Bottom message */}
        <Box
          mt="8"
          textAlign="center"
          bg="white"
          border="2px dashed"
          borderColor="gray.300"
          borderRadius="xl"
          p="8"
          maxW="700px"
          w="100%"
        >
          <Text fontSize="lg" color="gray.700" fontWeight="600">
            {content.bottomMessage}
          </Text>
        </Box>
      </VStack>
    </Container>
  </Box>
);
